// Kill Sheet Engine — composer (P3 of Kill Sheet Redev).
//
// Pure function: composeKillSheet(session) → KillSheetOutput.
//
// Contract:
//   - Refuses to run when validation rejects the session (returns
//     { ok: false, errors, warnings, output: null }).
//   - Calls only the deterministic formulas in lib/calc/iwcfKillSheet.js
//     and rounds for display via lib/calc/iwcfFormat.js. No I/O, no side
//     effects, no React imports — safe to use from CLI / Node tests.
//   - Snapshot-locked: the session is expected to contain fully-materialized
//     profile payloads (the wizard reads them via storage.getByVersion at
//     "Compute Kill" time and embeds them — they never change afterward).
//   - Returns a fully-structured KillSheetOutput document — see the JSON
//     preview script for a full example.

import {
  gravityFactor,
  gradientFromDensity,
  maxAllowableMudDensity, maaspFromLOT,
  killFluidDensity, killFluidGradient,
  initialCirculatingPressureIWCF, finalCirculatingPressureIWCF,
  kValuePer100Strokes, stepDownSchedule,
  pumpStrokesForVolume, surfaceLineStrokes, circulationTimeForStrokes,
  formationPressureFromSIDPP, fractureMargin, killMargin,
  sectionVolume, sumVolumes,
} from "@/lib/calc/iwcfKillSheet";
import { FMT, asFixed } from "@/lib/calc/iwcfFormat";
import { validateSession } from "./validate";

const _num = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : null);

// Convenience: { raw, display, formula, inputs? } cell builder. The optional
// `inputs` object preserves calculation provenance (per user req #2) so every
// major output can be traced back to its source values.
const cell = (raw, displayFn, formula, inputs) => ({
  raw,
  display: raw == null ? null : displayFn(raw),
  formula: formula || null,
  inputs:  inputs  || null,
});

// Safe call: any formula throw → null (returned as missing data, not error,
// since validation already enforced presence; this is defence-in-depth).
const safe = (fn) => { try { return fn(); } catch { return null; } };

export function composeKillSheet(session) {
  const v = validateSession(session);
  if (!v.ok) {
    return { ok: false, errors: v.errors, warnings: v.warnings, output: null };
  }

  const u = session.units;
  const well   = session.well   || {};
  const bha    = session.bha    || {};
  const csg    = session.casing || {};
  const mud    = session.mud    || {};
  const pump   = session.pump   || {};
  const pumpIdx = Number.isInteger(pump.selectedIndex) ? pump.selectedIndex : 0;
  const pumpSel = (pump.pumps || [])[pumpIdx] || {};
  const lens   = session.lengths    || {};
  const caps   = session.capacities || {};
  const kick   = session.kickData   || {};

  // ===========================================================================
  // 1. Formation Strength Data — (A), (B), (C), MAASP_initial.
  // ===========================================================================
  const lotPressure = _num(well.lotPressure);
  const mwAtTest    = _num(well.lotTestMudDensity);
  const shoeTvd     = _num(well.shoeTvd);
  const currentMW   = _num(mud.density);

  const maxMW = (lotPressure != null && mwAtTest != null && shoeTvd != null)
    ? safe(() => maxAllowableMudDensity({ lotPressure, mwAtTest, shoeTvd, units: u }))
    : null;

  const maaspInit = (maxMW != null && currentMW != null && shoeTvd != null)
    ? safe(() => maaspFromLOT({ maxMudDensity: maxMW, currentMW, shoeTvd, units: u }))
    : null;

  const formationStrength = {
    lotPressure:   cell(lotPressure, FMT.pressure, "Surface LOT (A)"),
    mwAtTest:      cell(mwAtTest,    FMT.density,  "MW at LOT test (B)"),
    shoeTvd:       cell(shoeTvd,     (v) => v,     "Shoe TVD"),
    maxMudDensity: cell(maxMW,
      FMT.density,
      "(C) = (A) / g / shoeTVD + (B)",
      { A: lotPressure, g: gravityFactor(u), shoeTVD: shoeTvd, B: mwAtTest }),
    maaspInitial:  cell(maaspInit,
      FMT.pressure2,
      "MAASP_0 = ((C) − ρ_curr) × shoeTVD × g",
      { C: maxMW, ρ_curr: currentMW, shoeTVD: shoeTvd, g: gravityFactor(u) }),
  };

  // ===========================================================================
  // 2. Current Well Data block (page-1 right column).
  // ===========================================================================
  const cmg = currentMW != null
    ? safe(() => gradientFromDensity({ density: currentMW, units: u }))
    : null;

  const currentWell = {
    md:              cell(_num(well.currentMd),  (v) => v),
    tvd:             cell(_num(well.currentTvd), (v) => v),
    shoeMd:          cell(_num(well.shoeMd),     (v) => v),
    shoeTvd:         cell(shoeTvd,               (v) => v),
    holeSize:        cell(_num(well.holeSize),   (v) => v),
    mudDensity:      cell(currentMW, FMT.density,  "ρ_curr"),
    mudGradient:     cell(cmg,       FMT.gradient, "g_curr = ρ_curr × g"),
    isSubsea:        !!well.isSubsea,
    subsea: well.isSubsea ? {
      waterDepth:      cell(_num(well.waterDepth),      (v) => v),
      riserIdIn:       cell(_num(well.riserIdIn),       (v) => v),
      riserCapacity:   cell(_num(well.riserCapacity),   (v) => v),
      chokeLineLength: cell(_num(well.chokeLineLength), (v) => v),
      chokeLineIdIn:   cell(_num(well.chokeLineIdIn),   (v) => v),
    } : null,
  };

  // ===========================================================================
  // 3. Pre-recorded volumes table (page-1 left column).
  // ===========================================================================
  const mkSection = (label, length, capacity) => {
    const L = _num(length), C = _num(capacity);
    const raw = (L != null && C != null && L > 0 && C > 0)
      ? safe(() => sectionVolume({ length: L, capacity: C })) : null;
    return {
      label, length: L, capacity: C,
      volume: cell(raw, FMT.volume, "V = length × capacity"),
    };
  };

  const sec_DP   = mkSection("Drill Pipe",            lens.dp,   caps.dpInternal);
  const sec_HWDP = mkSection("HWDP",                  lens.hwdp, caps.hwdpInternal);
  const sec_DC   = mkSection("Drill Collar",          lens.dc,   caps.dcInternal);
  const sec_DPOH = mkSection("DP/HWDP in Open Hole",  lens.dpHwdpOpenHole, caps.dpHwdpInOpenHole);
  const sec_DCOH = mkSection("DC in Open Hole",       lens.dcOpenHole,     caps.dcInOpenHole);
  const sec_DPCG = mkSection("DP in Casing",          lens.dpInCasing,     caps.dpInCasing);
  const sec_DCCG = mkSection("DC in Casing",          lens.dcInCasing,     caps.dcInCasing);

  const drillStringRaw = sumVolumes([sec_DP.volume.raw, sec_HWDP.volume.raw, sec_DC.volume.raw]);
  const openHoleRaw    = sumVolumes([sec_DPOH.volume.raw, sec_DCOH.volume.raw]);
  const inCasingRaw    = sumVolumes([sec_DPCG.volume.raw, sec_DCCG.volume.raw]);
  const totalAnnRaw    = openHoleRaw + inCasingRaw;
  const totalWellRaw   = drillStringRaw + totalAnnRaw;
  const activeSurfaceRaw = _num(mud.activeSurfaceVolume);
  const totalActiveRaw   = totalWellRaw + (activeSurfaceRaw || 0);

  const volumes = {
    sections: [sec_DP, sec_HWDP, sec_DC, sec_DPOH, sec_DCOH, sec_DPCG, sec_DCCG],
    drillStringVolume:  cell(drillStringRaw, FMT.volume,           "(D) = ΣV_drillstring"),
    openHoleVolume:     cell(openHoleRaw,    FMT.volume,           "(F) = ΣV_openhole"),
    inCasingVolume:     cell(inCasingRaw,    FMT.volume,           "(G) = ΣV_casing"),
    totalAnnulusVolume: cell(totalAnnRaw,    FMT.iwcfVolumeWhole,  "(H) = F + G"),
    totalWellSystem:    cell(totalWellRaw,   FMT.iwcfVolumeWhole,  "(I) = D + H"),
    activeSurface:      cell(activeSurfaceRaw, FMT.volume,         "(J) = active surface volume"),
    totalActiveFluid:   cell(totalActiveRaw, FMT.iwcfVolumeWhole,  "(I+J)"),
  };

  // ===========================================================================
  // 4. Pump strokes & circulation time.
  //    Strokes are computed from 2-dp-rounded section volumes per the IWCF
  //    convention (see iwcf_kill_sheet_golden.test.cjs for derivation).
  // ===========================================================================
  const disp = _num(pumpSel.displacement);
  const spm  = _num(pumpSel.spm);

  const _stkRow = (volDisplay) => {
    if (volDisplay == null || disp == null || disp <= 0)
      return { strokes: cell(null, FMT.strokes), time: cell(null, FMT.minutes) };
    const stk = safe(() => pumpStrokesForVolume({ volume: volDisplay, pumpDisplacement: disp }));
    const min = (stk != null && spm) ? safe(() => circulationTimeForStrokes({ strokes: stk, spm })) : null;
    return {
      strokes: cell(stk, FMT.strokes, "strokes = volume / pumpDisp"),
      time:    cell(min, FMT.minutes, "time = strokes / SPM"),
    };
  };

  const _stkRowFine = (volRaw) => {
    if (volRaw == null || disp == null || disp <= 0)
      return { strokes: cell(null, FMT.strokes), time: cell(null, FMT.fineMin) };
    const stk = safe(() => surfaceLineStrokes({ activeSurfaceVolume: volRaw, pumpDisplacement: disp }));
    const min = (stk != null && spm) ? safe(() => circulationTimeForStrokes({ strokes: stk, spm })) : null;
    return {
      strokes: cell(stk, FMT.strokes, "strokes = J / pumpDisp"),
      time:    cell(min, FMT.fineMin, "time = strokes / SPM"),
    };
  };

  const pumpStrokes = {
    dsv:              _stkRow(volumes.drillStringVolume.display),  // (E)
    openHole:         _stkRow(volumes.openHoleVolume.display),
    inCasing:         _stkRow(volumes.inCasingVolume.display),
    totalAnnulus:     _stkRow(FMT.volume(totalAnnRaw)),
    totalWellSystem:  _stkRow(FMT.volume(totalWellRaw)),
    surfaceLine:      _stkRowFine(activeSurfaceRaw),
    // The IWCF reference shows a +1 padding at this single cell (mathematically
    // 4146; IWCF prints 4147). We expose BOTH: the mathematical strokes and
    // the IWCF-padded display. The P4 PDF should show the padded value
    // alongside a footnote citing this engine's behavior.
    totalActiveFluid: (() => {
      const r = _stkRow(FMT.volume(totalActiveRaw));
      const rawStrokes = r.strokes.raw;
      const padded = rawStrokes != null ? Math.round(rawStrokes) + 1 : null;
      return {
        strokes:           r.strokes,
        strokesIwcfPadded: cell(padded, FMT.strokes, "IWCF reference shows +1 safety pad at this cell"),
        time:              r.time,
      };
    })(),
  };

  // ===========================================================================
  // 5. Kill calculations — page-2 box outputs.
  // ===========================================================================
  const sidpp = _num(kick.sidpp);
  const sicp  = _num(kick.sicp);
  const scr   = _num(pumpSel.slowPumpPressure);
  const tvd   = _num(well.currentTvd);

  const kmd_raw = (sidpp != null && tvd != null && currentMW != null)
    ? safe(() => killFluidDensity({ sidpp, tvd, currentMW, units: u })) : null;
  const kmg_raw = (cmg != null && sidpp != null && tvd != null)
    ? safe(() => killFluidGradient({ currentGradient: cmg, sidpp, tvd })) : null;
  const icp_raw = (sidpp != null && scr != null)
    ? safe(() => initialCirculatingPressureIWCF({ sidpp, dynamicPressureLoss: scr })) : null;
  const fcp_raw = (kmg_raw != null && cmg != null && scr != null)
    ? safe(() => finalCirculatingPressureIWCF({ killGradient: kmg_raw, currentGradient: cmg, dynamicPressureLoss: scr })) : null;
  const eStrokes_raw = pumpStrokes.dsv.strokes.raw;
  const k_raw = (icp_raw != null && fcp_raw != null && eStrokes_raw != null && eStrokes_raw > 0)
    ? safe(() => kValuePer100Strokes({ icp: icp_raw, fcp: fcp_raw, totalStrokesToBit: eStrokes_raw })) : null;

  // Step-down schedule (raw cells).
  let stepDown = [];
  if (icp_raw != null && fcp_raw != null && eStrokes_raw != null && eStrokes_raw > 0) {
    const sched = safe(() => stepDownSchedule({
      icp: icp_raw, fcp: fcp_raw, totalStrokesToBit: eStrokes_raw, intervalStrokes: 100,
    })) || [];
    stepDown = sched.map((r) => ({
      strokes:  cell(r.strokes,  FMT.strokes),
      pressure: cell(r.pressure, FMT.pressure),
    }));
  }

  const killCalc = {
    kmd:     cell(kmd_raw, FMT.density,  "KMD = SIDPP / TVD / g + ρ_curr",
                  { SIDPP: sidpp, TVD: tvd, g: gravityFactor(u), ρ_curr: currentMW }),
    kmg:     cell(kmg_raw, FMT.gradient, "KMG = ρ_curr × g + SIDPP / TVD",
                  { CMG: cmg, SIDPP: sidpp, TVD: tvd }),
    icp:     cell(icp_raw, FMT.pressure, "ICP = SIDPP + Dynamic Pressure Loss",
                  { SIDPP: sidpp, DPL: scr }),
    fcp:     cell(fcp_raw, FMT.pressure, "FCP = (KMG / CMG) × Dynamic Pressure Loss",
                  { KMG: kmg_raw, CMG: cmg, DPL: scr }),
    kPer100: cell(k_raw,   FMT.kPer100,  "K = (ICP − FCP) × 100 / (E)",
                  { ICP: icp_raw, FCP: fcp_raw, E: eStrokes_raw }),
    stepDown,
  };

  // ===========================================================================
  // 6. Additional outputs (Step-4 review card).
  // ===========================================================================
  const maaspNew_raw = (maxMW != null && kmd_raw != null && shoeTvd != null)
    ? safe(() => maaspFromLOT({ maxMudDensity: maxMW, currentMW: kmd_raw, shoeTvd, units: u })) : null;
  const bhp_raw = (sidpp != null && currentMW != null && tvd != null)
    ? safe(() => formationPressureFromSIDPP({ sidpp, currentMW, tvd, units: u })) : null;
  const frac_raw = (maaspInit != null && sicp != null)
    ? safe(() => fractureMargin({ maasp: maaspInit, sicp })) : null;
  const km_obj = (kmd_raw != null && currentMW != null && tvd != null)
    ? safe(() => killMargin({ killMudDensity: kmd_raw, currentMW, tvd, units: u })) : null;

  const additionalOutputs = {
    maaspNew:           cell(maaspNew_raw, FMT.pressure, "MAASP_new = ((C) − KMD) × shoeTVD × g"),
    formationPressure:  cell(bhp_raw,      FMT.pressure, "BHP = SIDPP + ρ_curr × g × TVD"),
    bottomHolePressure: cell(bhp_raw,      FMT.pressure, "BHP = SIDPP + ρ_curr × g × TVD"),
    fractureMargin:     cell(frac_raw,     FMT.pressure, "FracMargin = MAASP − SICP"),
    killMargin: km_obj ? {
      density:  cell(km_obj.density,  FMT.density,  "ΔMW = KMD − ρ_curr"),
      pressure: cell(km_obj.pressure, FMT.pressure, "ΔP  = ΔMW × g × TVD"),
    } : null,
  };

  // ===========================================================================
  // 7. Top-level output document.
  // ===========================================================================
  // Compute derived lengths (already validated; safe to do raw math here).
  const md         = _num(well.currentMd);
  const shoeMd     = _num(well.shoeMd);
  const bhaLength  = Array.isArray(bha.components)
    ? bha.components.reduce((s, c) =>
        s + (_num(c.length) != null ? _num(c.length) : 0), 0)
    : 0;
  const dpLengthDerived = (md != null && bhaLength)
    ? Math.max(0, md - bhaLength) : null;
  const openHoleLengthDerived = (md != null && shoeMd != null)
    ? Math.max(0, md - shoeMd) : null;

  // wellSnapshot — denormalized one-glance summary of the configured well per
  // user req #1.  Consumed by the wizard's Step-3 review page and the PDF
  // footer summary line.
  const wellSnapshot = {
    wellName:        well.label || "",
    md:              _num(well.currentMd),
    tvd:             _num(well.currentTvd),
    shoeMd:          shoeMd,
    shoeTvd:         _num(well.shoeTvd),
    holeSize:        _num(well.holeSize),
    mudDensity:      currentMW,
    mudGradient:     cmg,
    bhaLength,
    dpLength:        dpLengthDerived,
    openHoleLength:  openHoleLengthDerived,
    isSubsea:        !!well.isSubsea,
  };

  return {
    ok: true,
    errors: [],
    warnings: v.warnings,
    output: {
      meta: {
        sessionId:   session.id || null,
        variant:     session.variant,
        units:       u,
        wellName:    well.label || "",
        date:        session.date || new Date().toISOString().slice(0, 10),
        generatedAt: new Date().toISOString(),
        refs:        session.refs || null,
      },
      wellSnapshot,
      formationStrength,
      currentWell,
      pump: {
        label:            pumpSel.label || `Pump #${pumpIdx + 1}`,
        displacement:     cell(disp, FMT.capacity, ""),
        slowPumpRate:     cell(spm,  FMT.strokes, "SPM"),
        slowPumpPressure: cell(scr,  FMT.pressure, "Dynamic Pressure Loss"),
      },
      kickData: {
        sidpp:   cell(sidpp,                 FMT.pressure, ""),
        sicp:    cell(sicp,                  FMT.pressure, ""),
        pitGain: cell(_num(kick.pitGain),    FMT.volume,   ""),
      },
      volumes,
      pumpStrokes,
      killCalc,
      additionalOutputs,
      // Convenience: a flattened printable map for the PDF generator. Each
      // entry is what the P4 PDF will render in its corresponding IWCF cell.
      printable: {
        // top of page-1 left column
        formA:   asFixed(lotPressure, 0),
        formB:   asFixed(mwAtTest, 0),
        formC:   asFixed(maxMW, 3),
        maasp0:  asFixed(maaspInit, 2),
        // top of page-1 right column
        cmd:     asFixed(currentMW, 0),
        cmg:     asFixed(cmg, 4),
        // page-2 kill boxes
        kmd:     asFixed(kmd_raw, 2),
        kmg:     asFixed(kmg_raw, 4),
        icp:     asFixed(icp_raw, 0),
        fcp:     asFixed(fcp_raw, 0),
        kPer100: asFixed(k_raw, 2),
      },
    },
  };
}
